import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { useAppManager } from 'core/hooks';
import { Link } from 'core/routing';

import { Heading, Button } from 'components';

import { Table } from 'libs/connection';
import { SortByAction, FilterByAction } from 'libs/connection/actions';

import { ChangePortalBanner } from 'apps/client/components/ChangePortalBanner';

import styled from 'styled-components';

import { useClientStore, useRouterStore } from 'apps/hooks';

import { CenteredButtonWrapper } from 'apps/components/shared';

import { RFQConnManager } from './connManager';
import { ShipmentQuoteButton } from '../ShipmentQuoteButton';

const ListWrapper = styled.div`
  margin-top: 2rem;
`;

const HeadingWrapper = styled.div`
  display: flex;
  direction: row;
  justify-content: space-between;
`;

const CreateRFQAction = () => {
  return (
    <Link name='make-part'>
      <Button
        data-test-id='new-rfq-button'
        text='New RFQ'
        size='small'
        icon='add'
        iconPosition='right'
      />
    </Link>
  );
};

const baseActions = {
  leftActions: [SortByAction, FilterByAction],
  rightActions: [CreateRFQAction],
};

const RFQList = observer(
  ({ maxItems, disableSortingFilteringFeatures = false }) => {
    const clientStore = useClientStore();
    const routerStore = useRouterStore();

    const conn = useAppManager(RFQConnManager, null);

    const { activeSpace } = clientStore;
    const isIntermediator = clientStore.currentPortal === 'INTERMEDIATOR';
    const isInitiative = activeSpace?.spaceType === 'INITIATIVE';
    const title = isInitiative ? `RFQs` : 'My RFQs';

    let bottomButton = null;
    let numRows = conn?.nodes?.length;
    if (!numRows) numRows = 0;
    if (maxItems && maxItems <= numRows) {
      const params = { spacePath: clientStore.activeSpace?.path };
      bottomButton = (
        <CenteredButtonWrapper>
          <Link name='workspace.rfqs' params={params}>
            <Button
              size='default'
              variant='primary'
              outline
              text='Show all'
              icon='arrow_right'
              iconPosition='right'
            />
          </Link>
        </CenteredButtonWrapper>
      );
    }
    let changePortalBanner = null;
    if (routerStore.route.name !== 'workspace') {
      changePortalBanner = <ChangePortalBanner />;
    }

    let headerAction = null;
    let actionButtons = { ...baseActions };

    if (isIntermediator) {
      actionButtons = {
        ...actionButtons,
        rightActions: [ShipmentQuoteButton, CreateRFQAction],
      };
    } else {
      actionButtons = { ...baseActions };
    }

    if (disableSortingFilteringFeatures) {
      headerAction = <CreateRFQAction />;
      actionButtons = null;
    }

    return (
      <ListWrapper>
        <HeadingWrapper>
          <Heading level={2} size='large'>
            {title}
          </Heading>
          {headerAction}
        </HeadingWrapper>
        {changePortalBanner}
        <Table
          {...{
            contentName: 'RFQs',
            columns: conn.finalColumns,
            conn,
            actions: actionButtons,
            maxRows: maxItems,
          }}
        />
        {bottomButton}
      </ListWrapper>
    );
  },
);

RFQList.propTypes = {
  routerStore: PropTypes.object,
};

export default RFQList;
